/**
 * CallbackRegistry — the host side of the `invokeCallback` ActionFlow action
 * (Lane B-5, reconciled callback_action: corpus `onApprove` / `onReject`).
 *
 * Callbacks are BLOCK-INSTANCE-SCOPED: a global hook would shadow `onApprove`
 * across multiple cards. Each entry is keyed by `${blockId}::${name}`. A handler
 * registered with `blockId = null` is a GLOBAL fallback (matches any block) so
 * simple single-block hosts can register once without tracking block ids.
 *
 * `invoke` resolves block-scoped first, then the global fallback.
 */

export type HostCallback = (args?: unknown) => void

const GLOBAL = "" // null blockId normalizes to "" — the global-fallback bucket

export class CallbackRegistry {
  private readonly callbacks = new Map<string, HostCallback>()

  private key(blockId: string | null, name: string): string {
    return `${blockId ?? GLOBAL}::${name}`
  }

  /**
   * Register a named callback. Pass `blockId = null` for a global handler that
   * matches any block. Returns an unregister function (only removes if it is
   * still the active handler for that key).
   */
  register(blockId: string | null, name: string, callback: HostCallback): () => void {
    const k = this.key(blockId, name)
    this.callbacks.set(k, callback)
    return () => {
      if (this.callbacks.get(k) === callback) this.callbacks.delete(k)
    }
  }

  /**
   * Invoke a callback. Resolution: block-scoped (`blockId::name`) first, then the
   * global fallback (`::name`), then — when exactly ONE handler for `name` is
   * registered page-wide — that one (the common single-handler case, race-free
   * even if the dispatching block was mis-resolved). Only multiple blocks
   * registering the SAME name rely on a correct blockId; see app-runtime's
   * currentDispatchBlockNs note. Returns whether a handler ran.
   */
  invoke(blockId: string | null, name: string, args?: unknown): boolean {
    let callback = this.callbacks.get(this.key(blockId, name)) ?? this.callbacks.get(this.key(null, name))
    if (!callback) {
      const suffix = `::${name}`
      const matches: HostCallback[] = []
      for (const [k, cb] of this.callbacks) if (k.endsWith(suffix)) matches.push(cb)
      if (matches.length === 1) callback = matches[0]
    }
    if (!callback) {
      // eslint-disable-next-line no-console
      console.warn(`[CallbackRegistry] no handler for "${name}" (block ${blockId ?? "—"})`)
      return false
    }
    try {
      callback(args)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[CallbackRegistry] handler "${name}" threw`, err)
    }
    return true
  }

  clear(): void {
    this.callbacks.clear()
  }
}
